import { useReducer, createContext, JSX, useContext } from 'react';
import { Role } from 'model/Role';
import { User } from 'model/User';
import { Partner } from 'model/Partner';
import { Ledger } from 'model/Ledger';
import { Report } from 'model/Report';
import { Contractor } from 'model/Contractor';
import { WorkProcess } from 'model/WorkProcess';
import { WorkResource } from 'model/WorkResource';
import { LineRelation } from 'model/Relation';

export const sortType = { asc: 1, desc: 2 };

export type RecordListTypes = {
  user: { type: User };
  partner: { type: Partner };
  ledger: { type: Ledger };
  report: { type: Report };
  contractor: { type: Contractor },
  workProcess: { type: WorkProcess };
  workResource: { type: WorkResource };
  lineRelation: { type: LineRelation };
};

export type RecordListType = keyof RecordListTypes;

export type Sort = {
  order: typeof sortType[keyof typeof sortType];
  key: string;
};

export type RecordListPayload = {
  conditions?: { [key: string]: string | number | string[] | number[] };
  sorts: Sort[];
  limit: number | null;
  page: number;
};

export type RecordListParameter<T extends RecordListType> = RecordListPayload & {
  list: RecordListTypes[T]['type'][];
  perPage: number;
};

const makeInitialRecordListParameter = <T extends RecordListType>(defaultSorts?: Sort[]): RecordListParameter<T> => ({
  list: [],
  sorts: [...defaultSorts || []],
  limit: 20,
  page: 1,
  perPage: 1,
});

export type DataStore = {
  auth: {
    user?: User;
    status: 'logined' | 'logouted' | null;
    failedAlert?: boolean;
  };
  role: { list: Role[] };
  client: { lineCodeImageUrl?: string };
  user: RecordListParameter<'user'>;
  partner: RecordListParameter<'partner'>;
  ledger: RecordListParameter<'ledger'>;
  report: RecordListParameter<'report'>;
  contractor: RecordListParameter<'contractor'>;
  workProcess: RecordListParameter<'workProcess'>;
  workResource: RecordListParameter<'workResource'>;
  lineRelation: RecordListParameter<'lineRelation'>;
};

export type ReducerActionPayload = {
  SET_AUTH: { user: User };
  RESET_AUTH: { failedAlert: boolean } | null;
  SET_ROLE_LIST: Role[];
  SET_CLIENT: Partial<DataStore['client']>;
  SET_PAGE_LIST:
    { type: 'user', payload: Partial<RecordListParameter<'user'>> } |
    { type: 'partner', payload: Partial<RecordListParameter<'partner'>> } |
    { type: 'ledger', payload: Partial<RecordListParameter<'ledger'>> } |
    { type: 'report', payload: Partial<RecordListParameter<'report'>> } |
    { type: 'contractor', payload: Partial<RecordListParameter<'contractor'>> } |
    { type: 'workProcess', payload: Partial<RecordListParameter<'workProcess'>> } |
    { type: 'workResource', payload: Partial<RecordListParameter<'workResource'>> } |
    { type: 'lineRelation', payload: Partial<RecordListParameter<'lineRelation'>> };
};

type ReducerActionType = keyof ReducerActionPayload;

type ReducerAction<T extends ReducerActionType> = {
  type: T;
  payload: ReducerActionPayload[T];
};

type DataStoreContext<T extends ReducerActionType> = {
  state: DataStore;
  dispatch: React.Dispatch<ReducerAction<T>>;
};

export const reducer: <T extends ReducerActionType>(state: DataStore, action: ReducerAction<T>) => DataStore = (state, action) => {
  switch (action.type) {
    case 'SET_AUTH':
      return { ...state, auth: { ...action.payload as ReducerActionPayload['SET_AUTH'], status: 'logined', failedAlert: false } };
    case 'RESET_AUTH':
      return { ...state, auth: { user: undefined, status: 'logouted', ...(action.payload || {}) } };
    case 'SET_ROLE_LIST':
      return { ...state, role: { ...state.role, list: (action.payload || []) as Role[] } };
    case 'SET_CLIENT':
      return { ...state, client: { ...action.payload as ReducerActionPayload['SET_CLIENT'] } };
    case 'SET_PAGE_LIST':
      const pagePayload = action.payload as ReducerActionPayload['SET_PAGE_LIST'];

      return { ...state, [pagePayload.type]: { ...state[pagePayload.type], ...pagePayload.payload } };
    default:
      return state;
  }
};

const initialState: DataStore = {
  auth: {
    status: null,
  },
  role: { list: [] },
  client: {},
  user: makeInitialRecordListParameter<'user'>([{ key: 'id', order: 1 }]),
  partner: makeInitialRecordListParameter<'partner'>([{ key: 'id', order: 1 }]),
  ledger: makeInitialRecordListParameter<'ledger'>([{ key: 'id', order: 1 }]),
  report: makeInitialRecordListParameter<'report'>([{ key: 'startDatetime', order: 1 }]),
  contractor: makeInitialRecordListParameter<'contractor'>(),
  workProcess: makeInitialRecordListParameter<'workProcess'>(),
  workResource: makeInitialRecordListParameter<'workResource'>(),
  lineRelation: makeInitialRecordListParameter<'lineRelation'>(),
};

export const DataContext = createContext<Partial<DataStoreContext<ReducerActionType>>>({});

export const DataStoreContextProvider = (props: Required<{ children: React.ReactNode }>): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <DataContext.Provider value={{ state, dispatch }}>
      {props.children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);
